import styled from '@emotion/styled'
import { TheWestLetsGoTokyoLogo } from '../../logos/TheWestLetsGoTokyo/TheWestLetsGoTokyo'
import {
    TheWestLiveLogo,
    WEST_LIVE_LOGO_PRIMARY_FILL_CLASS,
    WEST_LIVE_LOGO_SECONDARY_FILL_CLASS,
} from '../../logos/TheWestLiveLogo/TheWestLiveLogo'
import { StyledWebLink } from '../../olympics/wan/MedalTally/MedalTally.styled'
import { SectionHeader } from '../../section-header/SectionHeader'
import { StyledSectionHeader } from '../../section-header/SectionHeader/SectionHeader.styled'
import { breakpoint } from '../../__styling'
import {
    ContainerWidth,
    metrics,
    ThemeMargins,
} from '../../__styling/settings/metrics'
import { calcRem } from '../../__styling/style-functions/calc-rem'

type Gutters = boolean | keyof ThemeMargins

export interface StyledThorProps {
    hasBackgroundFill?: boolean
    /** If true or undefined, will default to the outer margin of the theme */
    horizontalGutters?: Gutters
    /** If true or undefined, will default to the outer margin of the theme */
    verticalGutters?: Gutters
    verticalSpacing?: keyof ThemeMargins
    containerWidth?: ContainerWidth
    useStylingForHiddenPodcast?: boolean
}

interface ThemedMetrics {
    margins: ThemeMargins
}

const getGutterSize = (
    themedMetrics: ThemedMetrics,
    gutter?: Gutters,
): string | number => {
    return gutter === false
        ? 0
        : calcRem(
              typeof gutter === 'string'
                  ? themedMetrics.margins[gutter]
                  : themedMetrics.margins.outerMargin,
          )
}

export const StyledThor = styled('div')<StyledThorProps>(
    {
        margin: '0 auto',
        width: '100%',
        display: 'block',
    },
    ({
        theme,
        verticalSpacing,
        horizontalGutters,
        verticalGutters,
        hasBackgroundFill,
        containerWidth,
        useStylingForHiddenPodcast,
    }) => {
        const themedMetrics = metrics[theme.kind]

        return {
            marginBottom: useStylingForHiddenPodcast
                ? theme.margins.xl
                : verticalSpacing &&
                  calcRem(themedMetrics.margins[verticalSpacing]),
            paddingLeft: getGutterSize(themedMetrics, horizontalGutters),
            paddingRight: getGutterSize(themedMetrics, horizontalGutters),
            paddingTop: getGutterSize(themedMetrics, verticalGutters),
            paddingBottom: useStylingForHiddenPodcast
                ? undefined
                : getGutterSize(themedMetrics, verticalGutters),
            backgroundColor: hasBackgroundFill
                ? theme.colors.background.secondary
                : 'transparent',
            color: theme.colors.text.primary,
            maxWidth: containerWidth,
        }
    },
)

// DPT-3630 olympics customisations
export const StyledTokyoContainer = styled('div')(({ theme }) => ({
    alignItems: 'center',
    background: theme.colors.background.secondary,
    color: theme.colors.text.primary,
    display: 'flex',
    justifyContent: 'space-between',
    padding: calcRem(theme.margins.lg, theme.margins.md, theme.margins.md),
}))

export const StyledLetsGoTokyoLogo = styled(TheWestLetsGoTokyoLogo)({
    height: 36,
    width: 165,
})

export const StyledTokyoSectionHeader = styled(SectionHeader)({
    display: 'none',

    [breakpoint('sm')]: {
        display: 'block',
        width: 'auto',
    },

    [`& ${StyledSectionHeader}::before`]: {
        display: 'none',
    },
})

const footerLayoutBreakpoint = 'xxs'
export const StyledTokyoFooter = styled(StyledTokyoContainer)(({ theme }) => ({
    justifyContent: 'center',
    marginBottom: theme.margins.xl,
    padding: calcRem(theme.margins.md, theme.margins.md, theme.margins.xl),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gridTemplateAreas: `'westlive text'
                        'button button'`,

    [breakpoint(footerLayoutBreakpoint)]: {
        gridTemplateAreas: `'westlive text'
                            '. text'`,
    },

    [`& .${WEST_LIVE_LOGO_PRIMARY_FILL_CLASS}`]: {
        fill: theme.colors.text.primary,
    },

    [`& .${WEST_LIVE_LOGO_SECONDARY_FILL_CLASS}`]: {
        fill: theme.colors.text.primary,
    },
}))

export const StyledTheWestLiveLogo = styled(TheWestLiveLogo)(({ theme }) => ({
    gridArea: 'westlive',
    height: 56,
    width: 161,
    marginBottom: calcRem(theme.margins.sm),
    marginRight: calcRem(theme.margins.sm),

    [breakpoint(footerLayoutBreakpoint)]: {
        justifySelf: 'end',
        margin: 0,
        marginRight: calcRem(theme.margins.xl),
        gridRow: '1 / 3',
    },
}))

export const StyledTokyoFooterText = styled('div')(({ theme }) => ({
    gridArea: 'text',
    lineHeight: calcRem(18),
    marginBottom: calcRem(theme.margins.sm),
    marginLeft: calcRem(theme.margins.sm),

    [breakpoint(footerLayoutBreakpoint)]: {
        gridArea: 'text',
        gridRow: 1,
    },

    [breakpoint('sm')]: {
        marginLeft: 0,
    },
}))

export const StyledListenButton = styled(StyledWebLink)(({ theme }) => ({
    backgroundColor: theme.colors.actions.button.secondary!.background.default,
    border: `1px solid ${theme.colors.actions.button.secondary!.border}`,
    color: theme.colors.actions.button.secondary!.text.default,
    display: 'block',
    gridArea: 'button',
    margin: 0,

    '&:not(:first-child)': {
        marginLeft: 0,
    },

    '&:hover': {
        backgroundColor:
            theme.colors.actions.button.secondary!.background.hover,
        color: theme.colors.actions.button.secondary!.text.hover,
    },

    '&:focus': {
        backgroundColor:
            theme.colors.actions.button.secondary!.background.hover,
        color: theme.colors.actions.button.secondary!.text.hover,
    },

    [breakpoint(footerLayoutBreakpoint)]: {
        gridArea: 'text',
        gridRow: 2,
        width: 'auto',
        justifySelf: 'start',
    },
}))
